/** Coordinates browser permission, Push API state, durable storage, and sending. */

export interface StoredPushSubscription {
  endpoint: string;
  expirationTime: number | null;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface PushSubscriptionRepository {
  save: (subscription: StoredPushSubscription) => Promise<void>;
  remove: (endpoint: string) => Promise<void>;
}

export type PushStatus = 'unsupported' | 'disabled' | 'denied' | 'enabled';

export interface PushSendResult {
  sent: number;
  expired: number;
  failed: number;
}

export interface PushBrowser {
  isSupported: () => boolean;
  permission: () => NotificationPermission;
  requestPermission: () => Promise<NotificationPermission>;
  getSubscription: () => Promise<StoredPushSubscription | null>;
  subscribe: (publicKey: string) => Promise<StoredPushSubscription>;
  unsubscribe: () => Promise<boolean>;
}

export interface PushRemote {
  getPublicKey: () => Promise<string>;
  sendTest: () => Promise<PushSendResult>;
}

export interface PushNotifications {
  getStatus: () => Promise<PushStatus>;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  sendTest: () => Promise<PushSendResult>;
}

export const createPushNotifications = (
  browser: PushBrowser,
  repository: PushSubscriptionRepository,
  remote: PushRemote,
): PushNotifications => {
  let publicKeyRequest: Promise<string> | undefined;
  const persistedEndpoints = new Set<string>();
  const getPublicKey = (): Promise<string> => {
    publicKeyRequest ??= remote.getPublicKey().catch((error: unknown) => {
      publicKeyRequest = undefined;
      throw error;
    });
    return publicKeyRequest;
  };

  return {
    getStatus: async () => {
      if (!browser.isSupported()) return 'unsupported';
      if (browser.permission() === 'denied') return 'denied';
      const subscription = await browser.getSubscription();
      if (subscription !== null) {
        if (!persistedEndpoints.has(subscription.endpoint)) {
          await repository.save(subscription);
          persistedEndpoints.add(subscription.endpoint);
        }
        return 'enabled';
      }

      // Warm the public key before the user clicks. Permission remains directly
      // user-triggered, and subscribe() does not wait on a network round trip.
      await getPublicKey();
      return 'disabled';
    },

    enable: async () => {
      if (!browser.isSupported()) throw new Error('Push notifications are unavailable here.');
      const permission = await browser.requestPermission();
      if (permission !== 'granted') throw new Error('Notification permission was not granted.');

      const subscription = await browser.subscribe(await getPublicKey());
      await repository.save(subscription);
      persistedEndpoints.add(subscription.endpoint);
    },

    disable: async () => {
      const subscription = await browser.getSubscription();
      if (subscription === null) return;
      if (!(await browser.unsubscribe())) {
        throw new Error('The browser could not remove notifications.');
      }
      await repository.remove(subscription.endpoint);
      persistedEndpoints.delete(subscription.endpoint);
    },

    sendTest: () => remote.sendTest(),
  };
};
